import { useRenderedDiceStore } from "@/hooks/useRenderedDiceStore";
import { invalidate, useFrame } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import { type Group } from "three";
import { useShallow } from "zustand/shallow";
import {
  advanceAnimatedFrameController,
  createAnimatedFrameController,
  getAnimatedFrameData,
  recordAnimatedFrameTick,
  startAnimatedFrameController,
  writeAnimatedFrameTransform,
  type AnimatedFrameController,
} from "../helpers/animatedFrameData";

export const useAnimatedFrameData = (
  id: string,
  isDieNodeReady: boolean,
  onAnimationStarted?: () => void,
  onAnimationComplete?: () => void,
  preview?: boolean,
) => {
  const frameData = useRenderedDiceStore(
    useShallow((state) =>
      getAnimatedFrameData(state.renderedDice.entities[id]?.frameData),
    ),
  );
  const positionGroupRef = useRef<Group>(null!);
  const quaternionGroupRef = useRef<Group>(null!);
  const controllerRef = useRef<AnimatedFrameController>(
    createAnimatedFrameController(frameData),
  );
  const currentFrameRef = useRef(0);

  useEffect(() => {
    const controller = createAnimatedFrameController(frameData);
    controllerRef.current = controller;
    currentFrameRef.current = 0;

    if (preview) return;

    writeAnimatedFrameTransform({
      controller,
      nowMs: 0,
      targetPosition: positionGroupRef.current.position,
      targetQuaternion: quaternionGroupRef.current.quaternion,
    });
  }, [frameData, preview]);

  // Kicks off our useFrame to avoid occasional pauses in first die animation
  useEffect(() => {
    const controller = controllerRef.current;

    if (
      preview ||
      !isDieNodeReady ||
      controller.completed ||
      !controller.frameData.length
    ) {
      return;
    }

    invalidate();
  }, [frameData, isDieNodeReady, preview]);

  useFrame(({ clock }) => {
    const controller = controllerRef.current;

    if (
      preview ||
      !isDieNodeReady ||
      controller.completed ||
      !controller.frameData.length
    ) {
      return;
    }

    currentFrameRef.current = recordAnimatedFrameTick(controller);

    const elapsedMs = clock.getElapsedTime() * 1000;

    if (!controller.started) {
      if (startAnimatedFrameController(controller)) {
        onAnimationStarted?.();
      }
    }

    const result = advanceAnimatedFrameController(controller, elapsedMs);
    if (result.completed) {
      onAnimationComplete?.();
    }

    writeAnimatedFrameTransform({
      controller,
      nowMs: elapsedMs,
      targetPosition: positionGroupRef.current.position,
      targetQuaternion: quaternionGroupRef.current.quaternion,
    });

    invalidate();
  });

  return { positionGroupRef, quaternionGroupRef, currentFrameRef };
};
